/**
 * Settings ▸ Test Pricing ▸ Contract Lab Test Pricing — the pure parts of the G5
 * batch-edit grid (SuboutPOController on ContractLabTestPricing_ListView): row
 * shape, which rows are unsaved, the client-side checks and the Save payload.
 * The server repeats every check; these only stop a doomed round trip.
 */

export const CLP_PAGE_ID = 'set-contractlab-test-pricing';
export const CLP_FORM_NAME = 'Contract Lab Test Pricing';
export const CLP_BASE = '/api/v1/contractlab-test-pricings';

/** G5 message when nothing is ticked (LDMMessages "selectchkbox"). */
export const SELECT_CHECKBOX = 'Select at least one CheckBox';

/** The G5 grid's editable cells (AllowEdit in ContractLabTestPricing_ListView). */
export interface ClpEditable {
  /** TAT name — the grid edits names; ids are resolved on Save. */
  tat: string;
  /** Unit price as typed. */
  unitPrice: string;
  /** Contract lab name. */
  contractLab: string;
  comment: string;
}

export interface ClpRow extends ClpEditable {
  /** Stable grid key: the Oid, or "new-n" for a row added with Add and not saved. */
  key: string;
  /** dbo.ContractLabTestPricing Oid; null until saved. */
  id: string | null;
  /** The test method Oid — sent only for an added row (a saved price keeps its test). */
  testMethodId: string | null;
  priceCode: string;
  matrix: string;
  test: string;
  method: string;
  parameters: string;
  modifiedDate: string | null;
  changePercent: string;
}

/** One row of the G5 "Tests" picker (TestMethod_ListView_ContractPricing_New). */
export interface ClpTestPick {
  id: string;
  test: string | null;
  matrix: string | null;
  method: string | null;
  component: string | null;
  category: string | null;
  isFieldTest: boolean;
  testCode: string | null;
}

export interface ClpOption { id: string; name: string }

const str = (v: unknown): string => (v == null ? '' : String(v));

/** A unit price as the grid shows it: two decimals, like G5's $0.00 (blank stays blank). */
export function formatPrice(v: unknown): string {
  const s = str(v).trim();
  if (s === '') return '';
  const n = Number(s);
  return Number.isFinite(n) ? n.toFixed(2) : s;
}

/** A price as G5's grid shows it at rest: $ and two decimals ($10.30). */
export const priceText = (v: string): string => (v.trim() === '' ? '' : '$' + formatPrice(v));

/** G5's list order (ContractLabTestPricing_ListView Sorting): the number after "CP"
 *  (the BO's Code, 0 when there is no code), then Matrix, Test, Method. */
export function sortClpRows(rows: ClpRow[]): ClpRow[] {
  const code = (r: ClpRow) => { const n = Number(r.priceCode.slice(2)); return r.priceCode && Number.isFinite(n) ? n : 0; };
  const cmp = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
  return [...rows].sort((a, b) => code(a) - code(b) || cmp(a.matrix, b.matrix) || cmp(a.test, b.test) || cmp(a.method, b.method));
}

/** A list row from GET /api/v1/modules/set-contractlab-test-pricing. */
export function toClpRow(r: Record<string, unknown>): ClpRow {
  const id = str(r.oid);
  return {
    key: id,
    id,
    testMethodId: null,
    priceCode: str(r.priceCode),
    matrix: str(r.matrix),
    test: str(r.test),
    method: str(r.method),
    parameters: str(r.parameters),
    tat: str(r.tat),
    unitPrice: formatPrice(r.unitPrice),
    contractLab: str(r.contractLab),
    modifiedDate: r.modifiedDate == null ? null : str(r.modifiedDate),
    changePercent: str(r.changePercent),
    comment: str(r.comment),
  };
}

/** A row for one test picked in Add — unsaved, no price code yet (it is minted on Save). */
export function newClpRow(seq: number, t: ClpTestPick): ClpRow {
  return {
    key: `new-${seq}`,
    id: null,
    testMethodId: t.id,
    priceCode: '',
    matrix: t.matrix ?? '',
    test: t.test ?? '',
    method: t.method ?? '',
    parameters: '',
    tat: '',
    unitPrice: '0.00',
    contractLab: '',
    modifiedDate: null,
    changePercent: '0',
    comment: '',
  };
}

const samePrice = (a: string, b: string) => {
  const x = Number(a.trim() === '' ? 0 : a), y = Number(b.trim() === '' ? 0 : b);
  return Number.isFinite(x) && Number.isFinite(y) ? x === y : a.trim() === b.trim();
};

/** Rows to send on Save: every added row, and every saved row whose editable cells differ from load. */
export function changedClpRows(rows: ClpRow[], loaded: Map<string, ClpRow>): ClpRow[] {
  return rows.filter((r) => {
    if (r.id == null) return true;
    const o = loaded.get(r.key);
    if (!o) return false;
    return r.tat !== o.tat || r.contractLab !== o.contractLab
      || r.comment.trim() !== o.comment.trim() || !samePrice(r.unitPrice, o.unitPrice);
  });
}

/** The first problem that would make Save fail, worded as the server words it; null when fine. */
export function firstClpProblem(rows: ClpRow[]): string | null {
  for (const r of rows) {
    const label = r.priceCode || r.test || 'New row';
    if (!r.tat.trim()) return `${label}: TAT must not be empty.`;
    if (!r.contractLab.trim()) return `${label}: Contract Lab must not be empty.`;
    if (r.unitPrice.trim() !== '' && !Number.isFinite(Number(r.unitPrice))) return `${label}: Unit Price must be a number.`;
  }
  return null;
}

/** The Save body for one row: names turned back into the TAT / contract lab ids. */
export function toClpInput(r: ClpRow, tats: ClpOption[], labs: ClpOption[]) {
  const idOf = (list: ClpOption[], name: string) => list.find((o) => o.name === name)?.id ?? null;
  return {
    id: r.id,
    testMethod: r.id == null ? r.testMethodId : null,
    tat: idOf(tats, r.tat),
    contractLab: idOf(labs, r.contractLab),
    unitPrice: r.unitPrice.trim() === '' ? 0 : Number(r.unitPrice),
    comment: r.comment.trim() === '' ? null : r.comment,
  };
}

/** Search across the shown text; a row not saved yet always stays visible. */
export function filterClpRows(rows: ClpRow[], search: string): ClpRow[] {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => r.id == null
    || [r.priceCode, r.matrix, r.test, r.method, r.parameters, r.tat, r.unitPrice, r.contractLab, r.changePercent, r.comment]
      .some((v) => v.toLowerCase().includes(q)));
}
